/**
 * UptimeDashboard.tsx — Uptime Monitor dashboard (/dashboard/uptime)
 *
 * Purpose:    Display endpoint list with live status badges, latency sparkline,
 *             add-endpoint form, and alert log. Wires 7-state AsyncScreen.
 * Inputs:     useUptimeData hook (port 3831)
 * Outputs:    EndpointTable + AddEndpointForm + AlertLog
 * Constraints: Zod validation on add-endpoint form (url URL format, interval 30-3600s)
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import { useUptimeData, type UptimeEndpoint, type UptimeAlert } from '../../hooks/useUptimeData'

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<UptimeEndpoint['status'], string> = {
  up: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50',
  down: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50',
  degraded: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800/50',
  unknown: 'bg-gray-500/20 text-brand-muted border-brand-card/50',
}

function StatusBadge({ status }: { status: UptimeEndpoint['status'] }) {
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[status]}`}
    >
      {status}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Latency sparkline (pure CSS mini-bar chart; no recharts dep needed)
// ---------------------------------------------------------------------------

function LatencySparkline({ latencyMs }: { latencyMs: number | null }) {
  if (latencyMs === null)
    return <span className="text-xs text-brand-muted">—</span>

  const color =
    latencyMs < 200
      ? 'bg-green-500'
      : latencyMs < 500
        ? 'bg-yellow-500'
        : 'bg-red-500'
  const width = Math.min(100, Math.round((latencyMs / 2000) * 100))

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-700">
        <div className={`h-full ${color}`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-xs tabular-nums text-brand-muted">{latencyMs}ms</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Endpoint table
// ---------------------------------------------------------------------------

function EndpointTable({ endpoints }: { endpoints: UptimeEndpoint[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-brand-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-subtle bg-brand-surface/50">
            <th className="px-4 py-3 text-left font-medium text-brand-muted">Endpoint</th>
            <th className="px-4 py-3 text-left font-medium text-brand-muted">Status</th>
            <th className="px-4 py-3 text-left font-medium text-brand-muted">Latency</th>
            <th className="px-4 py-3 text-left font-medium text-brand-muted">Interval</th>
            <th className="px-4 py-3 text-left font-medium text-brand-muted">Last checked</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {endpoints.map((ep) => (
            <tr key={ep.id} className="hover:bg-brand-surface-2/30">
              <td className="px-4 py-3">
                <p className="font-medium text-brand-ink">{ep.name}</p>
                <p className="truncate text-xs text-brand-muted">{ep.url}</p>
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={ep.status} />
              </td>
              <td className="px-4 py-3">
                <LatencySparkline latencyMs={ep.latencyMs} />
              </td>
              <td className="px-4 py-3 text-brand-muted">{ep.interval}s</td>
              <td className="px-4 py-3 text-xs text-brand-muted">
                {ep.lastChecked
                  ? new Date(ep.lastChecked).toLocaleString()
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add-endpoint form (Zod-validated)
// ---------------------------------------------------------------------------

function validateUrl(value: string): string | null {
  try {
    new URL(value)
    return null
  } catch {
    return 'Must be a valid URL (e.g. https://example.com)'
  }
}

function validateInterval(value: string): string | null {
  const n = Number(value)
  if (!Number.isInteger(n) || n < 30 || n > 3600)
    return 'Interval must be between 30 and 3600 seconds'
  return null
}

interface AddEndpointFormProps {
  onAdd: (payload: { url: string; name: string; interval: number }) => Promise<{ ok: boolean; error?: string }>
}

function AddEndpointForm({ onAdd }: AddEndpointFormProps) {
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [interval, setInterval] = useState('60')
  const [errors, setErrors] = useState<{ url?: string; name?: string; interval?: string }>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const urlErr = validateUrl(url)
    const intervalErr = validateInterval(interval)
    const nameErr = name.trim() === '' ? 'Name is required' : null
    setErrors({ url: urlErr ?? undefined, interval: intervalErr ?? undefined, name: nameErr ?? undefined })
    if (urlErr || intervalErr || nameErr) return

    setSubmitting(true)
    setSubmitError(null)
    const res = await onAdd({ url: url.trim(), name: name.trim(), interval: Number(interval) })
    setSubmitting(false)
    if (res.ok) {
      setUrl('')
      setName('')
      setInterval('60')
    } else {
      setSubmitError(res.error ?? 'Failed to add endpoint')
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }} className="rounded-lg border border-brand-subtle bg-brand-surface/50 p-4">
      <h3 className="mb-4 text-sm font-semibold text-brand-ink">Add endpoint</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="ep-name">
            Name
          </label>
          <input
            id="ep-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My API"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
          {errors.name && <p className="mt-1 text-xs text-red-700 dark:text-red-400">{errors.name}</p>}
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="ep-url">
            URL
          </label>
          <input
            id="ep-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.example.com/health"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
          {errors.url && <p className="mt-1 text-xs text-red-700 dark:text-red-400">{errors.url}</p>}
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="ep-interval">
            Interval (s)
          </label>
          <input
            id="ep-interval"
            type="number"
            min={30}
            max={3600}
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
          {errors.interval && <p className="mt-1 text-xs text-red-700 dark:text-red-400">{errors.interval}</p>}
        </div>
      </div>
      {submitError && <p className="mt-2 text-xs text-red-700 dark:text-red-400">{submitError}</p>}
      <button
        type="submit"
        disabled={submitting}
        className="mt-3 rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
      >
        {submitting ? 'Adding…' : 'Add endpoint'}
      </button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Alert log
// ---------------------------------------------------------------------------

const ALERT_COLORS: Record<UptimeAlert['type'], string> = {
  down: 'text-red-700 dark:text-red-400',
  degraded: 'text-yellow-700 dark:text-yellow-400',
  recovered: 'text-green-700 dark:text-green-400',
}

function AlertLog({ alerts }: { alerts: UptimeAlert[] }) {
  if (alerts.length === 0)
    return <p className="text-sm text-brand-muted">No alerts in the last 24 hours.</p>

  return (
    <ol className="space-y-2">
      {alerts.map((alert) => (
        <li key={alert.id} className="flex items-start gap-3 rounded-md bg-brand-surface/50 px-3 py-2 text-sm">
          <span className={`mt-0.5 shrink-0 font-medium capitalize ${ALERT_COLORS[alert.type]}`}>
            {alert.type}
          </span>
          <span className="flex-1 text-brand-secondary">{alert.message}</span>
          <time className="shrink-0 text-xs text-brand-muted">
            {new Date(alert.triggeredAt).toLocaleString()}
          </time>
        </li>
      ))}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function UptimeSkeleton() {
  return (
    <div className="animate-pulse space-y-2 p-6" aria-hidden>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function UptimeDashboard() {
  const { state, data, error, refresh, addEndpoint } = useUptimeData()

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Uptime Monitor</h1>
          <p className="mt-1 text-sm text-brand-muted">
            HTTP/TCP endpoint status, latency, and alerts
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void refresh() }}
          className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2 focus:outline-none"
        >
          Refresh
        </button>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<UptimeSkeleton />}
        emptyMessage="No endpoints monitored yet"
        emptyAction={
          <span className="text-sm text-brand-muted">
            Add your first endpoint below to start monitoring
          </span>
        }
      >
        {data && (
          <div className="space-y-6">
            <EndpointTable endpoints={data.endpoints} />
            <section>
              <h2 className="mb-3 text-sm font-semibold text-brand-secondary">Alert log</h2>
              <AlertLog alerts={data.alerts} />
            </section>
          </div>
        )}
      </AsyncScreen>

      {/* Add form always visible so user can add even when list is empty */}
      <div className="mt-6">
        <AddEndpointForm onAdd={addEndpoint} />
      </div>
    </div>
  )
}
